import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import api from '../../../utils/api';

const ProviderUsers = () => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/consultation/history/${user?._id || user?.id}`);
      const data = response.data;
      if (data.success) {
        const list = data.data?.items ?? data.data;
        const consultations = Array.isArray(list) ? list : [];
        const uniquePatients = {};
        consultations.forEach(consultation => {
          if (consultation.patient && consultation.patient.id) {
            const patientId = consultation.patient.id;
            if (!uniquePatients[patientId]) {
              uniquePatients[patientId] = {
                ...consultation.patient,
                consultationCount: 0,
                lastConsultation: consultation.created_at || consultation.createdAt
              };
            }
            uniquePatients[patientId].consultationCount++;
            const consultationDate = new Date(consultation.created_at || consultation.createdAt);
            const lastDate = new Date(uniquePatients[patientId].lastConsultation);
            if (consultationDate > lastDate) {
              uniquePatients[patientId].lastConsultation = consultation.created_at || consultation.createdAt;
            }
          }
        });
        setPatients(Object.values(uniquePatients));
      } else {
        showError(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients.filter(patient =>
    patient.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        <p className="text-gray-400 mt-2">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">My Users</h1>

      <div className="bg-gray-800 rounded-lg p-4">
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
        />
      </div>

      <div className="space-y-4">
        {filteredPatients.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-xl">
            <p className="text-gray-400">
              {searchTerm ? 'No users found matching your search' : 'No users yet'}
            </p>
          </div>
        ) : (
          filteredPatients.map((patient) => (
            <div
              key={patient.id || patient._id}
              className="bg-gray-800 rounded-xl p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-dark">
                      {patient.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-light">{patient.name || 'User'}</h3>
                    <p className="text-sm text-gray-400">{patient.email || 'N/A'}</p>
                    {patient.phone && (
                      <p className="text-sm text-gray-400 mt-1">{patient.phone}</p>
                    )}
                    <div className="flex items-center space-x-4 mt-3">
                      <div className="text-sm">
                        <span className="text-gray-400">Sessions: </span>
                        <span className="text-gold font-medium">{patient.consultationCount || 0}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-400">Last visit: </span>
                        <span className="text-light">
                          {new Date(patient.lastConsultation).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProviderUsers;
